import {Component, Injectable, OnInit} from '@angular/core';
import {HierarchyService} from "../../../services/hierarchy.service";
import {FirmwareProcedure} from "../../../model/firmware-procedure";
import {FirmwareProcedureFilters} from "../../../model/filters/firmware-procedure-filters";
import {FirmwareService} from "../../../services/firmware.service";
import {TimezoneService} from "../../../services/timezone.service";
import {AuthGuard} from "../../../common/auth-guard.service";
import {TranslocoService} from "@ngneat/transloco";
import {FirmwareType} from "../../../model/firmware-type";
import {FirmwareProcedureState} from "../../../model/firmware-procedure-state";
import {interval, Subscription} from "rxjs";

@Component({
  selector: 'app-firmware-procedures-list',
  templateUrl: './firmware-procedures-list.component.html',
  styleUrls: ['./firmware-procedures-list.component.scss']
})
@Injectable({
  providedIn: 'root'
})
export class FirmwareProceduresListComponent implements OnInit {

  public firmwareProcedures: FirmwareProcedure[];
  public totalProcedures: number;
  public loadingProcedures: boolean;

  public hierarchy: any[];
  public organizationCodes: any[];
  public organizations: any[];

  public firmwareTypeOptions: { label: string, value: string }[] = [];
  public stateOptions: { label: string, value: string }[] = [];

  public rows: number = 10;
  public first: number = 0;

  public filtersVisible: boolean;
  public currentFilters: FirmwareProcedureFilters;

  public availableYearRange: string;
  public rangeDates: Date[];

  private updateSubscription: Subscription;


  constructor(private firmwareService: FirmwareService,
              private hierarchyService: HierarchyService,
              private i18n: TranslocoService,
              private readonly timezoneService: TimezoneService,
              public authGuard: AuthGuard) {
    this.currentFilters = new FirmwareProcedureFilters();
    this.availableYearRange = this.timezoneService.calcYearRange();
  }

  ngOnInit(): void {
    this.updateSubscription = interval(30000).subscribe(
      () => {
        this.loadProcedures();
      }
    );

    this.firmwareProcedures = new Array(this.rows);
    this.buildTreeNode();
    this.buildFirmwareTypeOptions();
    this.buildStateOptions();
    this.loadProcedures();
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      let responseHierarchy: any[] = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto);
      this.hierarchy = responseHierarchy[1];
      this.organizationCodes = responseHierarchy[2].sort((a, b) => a.label > b.label ? 1 : -1);
    });
  }

  private buildFirmwareTypeOptions(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.firmwareTypeOptions = [
        {label: this.i18n.translate('attribute.firmwareIot'), value: FirmwareType.FIRMWARE_IOT},
        {label: this.i18n.translate('attribute.firmwareEcu'), value: FirmwareType.FIRMWARE_MACHINE_ECU},
        {label: this.i18n.translate('attribute.firmwareElevator'), value: FirmwareType.FIRMWARE_MACHINE_ELEVATOR}
      ];
    });
  }

  private buildStateOptions(): void {
    this.i18n.selectTranslation().subscribe(() => {
      this.stateOptions = [
        {label: this.i18n.translate('ota.QUEUED'), value: FirmwareProcedureState.QUEUED},
        {label: this.i18n.translate('ota.STARTED'), value: FirmwareProcedureState.STARTED},
        {label: this.i18n.translate('ota.FINISHED'), value: FirmwareProcedureState.FINISHED},
        {label: this.i18n.translate('ota.FAILED'), value: FirmwareProcedureState.FAILED},
      ];
    });
  }

  private loadProcedures(offset?: number, limit?: number): void {
    this.loadingProcedures = true;
    this.firmwareProcedures = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.firmwareService.getFirmwareProcedures(offset, limit, this.currentFilters)
      .subscribe(response => {
        this.firmwareProcedures = response.data.content;
        this.totalProcedures = response.data.totalElements;
        this.loadingProcedures = false;
      });
  }

  trimFieldsForm(filters: FirmwareProcedureFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  onPageChange(event): void {
    this.loadProcedures(event.first / event.rows, event.rows);
  }

  applyFilters(): void {
    this.first = 0;
    if (this.organizations != null && this.organizations.length > 0) {
      this.currentFilters.organizationUuids = this.organizations.map(organization => organization.data);
    } else {
      delete this.currentFilters['organizationUuids'];
    }

    if (this.rangeDates) {
      let periodRange: { from: number, until: number };
      this.currentFilters.from = this.rangeDates[0].getTime();
      if (this.rangeDates.length == 1 || this.rangeDates[1] == null) {
        periodRange = this.timezoneService.timestampRangeFromCalendarData([this.rangeDates[0], this.rangeDates[0]]);
      } else {
        periodRange = this.timezoneService.timestampRangeFromCalendarData([this.rangeDates[0], this.rangeDates[1]]);
      }
      this.currentFilters.from = periodRange.from;
      this.currentFilters.until = periodRange.until;
    } else if (this.currentFilters.from) {
      this.currentFilters.from = null;
      this.currentFilters.until = null;
    }
    this.loadProcedures(this.first / this.rows, this.rows);
  }

  resetFilters(): void {
    this.currentFilters = new FirmwareProcedureFilters();
    this.organizations = [];
    this.rangeDates = undefined;
  }

  protected readonly FirmwareType = FirmwareType;
  protected readonly FirmwareProcedureState = FirmwareProcedureState;
}
